import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material";

/**
 * The one source for a navigable row's hover and selected surface — sidebar, in-page sub-nav,
 * and every dropdown menu item.
 *
 * ## Why this exists
 *
 * Three call sites each stated their own answer, and none of the three agreed:
 *
 *   main Sidebar          selected `primary[100]` (a solid grey), hover MUI `action.hover`
 *   SideNav pill          selected `primary[50]` (#f6f6f6) — except it never rendered, see below
 *   MuiMenuItem override  hover brand @0.12, selected brand @0.16, both modes
 *
 * `SideNav`'s grey never actually shipped: the theme-level `MuiMenuItem` override lands later in
 * the cascade at equal specificity and wins, so the settings nav has been rendering the brand
 * tint while its own source said grey. Whoever wrote it believed they had made it grey. That is
 * the failure mode this file prevents — the same one `fieldChrome.ts` was created for, where four
 * components each recorded "matches the shared Input" in a COMMENT and silently stopped matching
 * the moment the token moved.
 *
 * ## The values
 *
 * Brand at 8% selected / 4% hover, from lab option E (Internal → Labs → Settings nav), chosen
 * over the 16%/12% that shipped. Measured on the sidebar surface, brand @0.16 selected is:
 *
 *   light   1.250:1   ΔE 15.60
 *   dark    1.145:1   ΔE 18.17
 *
 * ΔE 15-18 against a just-noticeable-difference of ~2.3 makes a selected nav row a roughly
 * sevenfold-JND event — an enormous perceptual jump for what is meant to be a quiet state, and
 * the reason it read as the only coloured object on screen. In BOTH modes, which is worth
 * stressing: the earlier diagnosis blamed light alone, and the numbers do not support that.
 *
 * Selected is brand; hover is neutral. Amounts are per-mode. See the note below the header.
 */

/**
 * SELECTED is brand. HOVER is neutral. That split is the decision, not an inconsistency.
 *
 * Why they differ, argued both ways before landing:
 *
 * The case for brand on BOTH is real and structural — in light mode the entire luminance range
 * from the #f4f5f5 page to white is 1.092:1, so a neutral state has to fit inside an almost
 * exhausted budget (black @0.04 measures 1.092:1 at chroma 0), whereas a hue adds a second channel
 * that is not competing for luminance at all. Grey in this theme also already carries borders,
 * disabled, secondary text and surfaces; a colour with exactly one meaning is more learnable than
 * a sixth shade of grey.
 *
 * What decided it against brand-on-hover is FREQUENCY and CO-OCCURRENCE. Measured, brand at the
 * amounts that read as visible is a large perceptual event: the original @0.16 selected was ΔE
 * 15.6 in light and 18.2 in dark, roughly 7x the ~2.3 just-noticeable difference. That is
 * defensible for SELECTED, which is exactly one row at a time and means "you are here". It is not
 * defensible for hover, which fires on every pointer movement and can co-occur across a table row,
 * a nav row and a menu item at once — three simultaneous purple regions. And this console is full
 * of product photography and certificate imagery, where the CONTENT is the colour; chrome that
 * competes with it loses on the register the product is aiming at.
 *
 * So: hue marks the persistent, single-instance state; luminance marks the transient one.
 *
 * Amounts, measured on each mode's sidebar surface:
 *
 *   selected (brand)   light 0.12  1.146:1  ΔE  9.8      dark 0.20  1.276:1  ΔE 20.3
 *   hover (neutral)    light black 0.04  1.092:1  chroma 0   dark white 0.08  1.246:1  chroma 0
 *
 * Selected's light value sits between two rejected points: @0.08 was ΔE 6.5 and read too subtle,
 * @0.16 was ΔE 15.6 and read too loud.
 *
 * Note the states are close in LUMINANCE in dark (1.246 hover against 1.276 selected) and separate
 * almost entirely by hue there. That is intended — dark has room for a strong neutral, and the
 * chroma difference is what distinguishes them.
 *
 * Hover deliberately reads `theme.palette.action.hover` rather than restating the alphas. MUI
 * already flips base AND amount per mode there (black 4% light, white 8% dark), which is exactly
 * the behaviour wanted, and it is the value the sidebar used before any of this work — so the
 * neutral hover is a return to the platform default rather than a new invention.
 */
const SELECTED_AMOUNT = { light: 0.12, dark: 0.2 } as const

/**
 * Selected row surface — brand, because selection is persistent and single-instance.
 *
 * ⚠️ The parameter is NARROWED, not `Theme`, for the reason `dimNeutralText` documents at
 * length below: the repo resolves two `@mui/material@5.18.0` instances (one under react@18,
 * one under react@19), so the app's `Theme` and this package's are structurally identical and
 * nominally distinct. A `Theme` parameter therefore fails from `apps/platform` with
 * `TS2345 … 'Theme' is not assignable to … 'Theme'`, plus an intermittent
 * `TS2859 Excessive complexity comparing types 'Theme' and 'Theme'`.
 *
 * That was not theoretical here — `ProgressPill`'s `navSelected(theme)` call was already
 * throwing both, uncounted among the platform's ~1027 standing errors, and adding a second
 * caller in `TakeATourChip` reproduced them one-for-one. Narrowing fixes both call sites at
 * once and cannot break any existing one: every `Theme` satisfies `{palette: {mode}}`, so this
 * strictly WIDENS what the function accepts.
 *
 * It is also the honest signature. This needs a mode and a token, not a theme.
 */
export const navSelected = (theme: { palette: { mode: string } }): string =>
  alpha(
    (theme.palette as any).brand?.[500] || "#8F68EA",
    SELECTED_AMOUNT[theme.palette.mode === "light" ? "light" : "dark"],
  );

/**
 * Foreground (text AND icon) for content sitting ON a brand-tinted surface — anything whose
 * background is `navSelected` or a sibling brand alpha.
 *
 * ⚠️ THIS IS THE HALF THAT CANNOT BE AN ALPHA, AND THE HALF PEOPLE GET WRONG.
 *
 * `navSelected` solved the SURFACE by compositing brand over whatever is behind it, so one
 * expression serves both modes. A foreground has no such escape: it is read AGAINST that
 * surface, so it has to move in the opposite direction to it — darker as the tint lightens,
 * lighter as the tint darkens. A single ramp step therefore cannot serve both modes, and
 * picking one is picking a mode to fail in.
 *
 * Measured in the browser via `getComputedStyle`, compositing each layer over the real page
 * surface (#f4f5f5 light, #09090b dark) rather than reasoning about it:
 *
 *   brand[600] fixed (what the Tour chip shipped)   light 4.521:1   dark 3.464:1  ← FAILS AA
 *   brand[700] / brand[300] (this)                  light 5.491:1   dark 9.161:1
 *
 * The DARK figure is the defect, and note that light was never the problem — 4.521 scrapes
 * past AA. That asymmetry is exactly what a single fixed value buys you: it is tuned, however
 * implicitly, for whichever mode the author had open, and fails in the other one silently.
 * 3.464 is under WCAG AA's 4.5:1 for body text, so the label on a brand chip was
 * illegible-by-standard for every dark-mode user while looking entirely deliberate in source.
 *
 * ## Why this is a helper and not two inline ternaries
 *
 * `ProgressPill` derived this pair first and stated it inline. The Tour chip is the SECOND
 * surface wanting brand-foreground-on-brand-tint, which is §36's extract-on-the-second-
 * hand-roll trigger exactly. Both now call this, so the two cannot drift into different
 * purples — and a third caller gets the measured pair rather than re-deriving it, which is
 * how `brand[600]` got chosen the first time.
 *
 * Pairs with `navSelected`: use them together or neither. A `navSelected` background with a
 * hand-picked foreground is the defect this exists to prevent.
 */
export const brandTintText = (theme: { palette: { mode: string } }): string =>
  theme.palette.mode === "light"
    ? (theme.palette as any).brand?.[700] || "#6F36CB"
    : (theme.palette as any).brand?.[300] || "#C7BBF7";

/**
 * Border for a brand-tinted surface that is INTERACTIVE — a chip you click, not a lozenge you
 * read. `ProgressPill` has no border by design (passive status); anything with an affordance
 * keeps one, and this is its colour.
 *
 * ⚠️ THE HONEST FINDING, because the obvious diagnosis is wrong.
 *
 * The Tour chip's border was `alpha(brand[500], 0.3)` — a FIXED alpha, and it is tempting to
 * call that the same defect as the fixed `brand[600]` foreground one layer out. It is not. An
 * alpha composites over whatever is behind it, so it already adapts per mode; that is the very
 * property `navSelected` exists to exploit. A fixed alpha is not a fixed colour.
 *
 * What IS wrong is subtler, and it only appears once the FILL is corrected. A border paints over
 * the element's own background (`background-clip: border-box`), so its effective brand fraction
 * against the page is `a + (1 - a) * fill_a` — it depends on the fill. Raising the dark fill from
 * 0.08 to `navSelected`'s 0.20 therefore drags the dark border up with it, and nobody chose that.
 * Measured (border composited over fill, then against the real page):
 *
 *                              light    dark
 *   before, a = 0.30           1.509    1.577    ← the two modes agreed, near enough
 *   a = 0.30 on the NEW fill   1.562    1.835    ← what leaving it alone would have shipped
 *   THIS, a = 0.32 / 0.20      1.597    1.588
 *
 * Read the middle row: the dark edge would have jumped to 1.835 against light's 1.562, a spread
 * of 0.27, purely as a side-effect of a change made one property away. The per-mode alphas below
 * hold the two within 0.009 of each other instead.
 *
 * Note the dark alpha goes DOWN (0.30 → 0.20) while `navSelected`'s goes UP (0.12 → 0.20). That
 * is not an inconsistency: a fill is measured against the page alone, a border against the page
 * AND the fill beneath it, so the two arithmetics genuinely do not point the same way. Anyone
 * "fixing" the dark border back up to match the fill will re-open the 1.835.
 */
const TINT_BORDER_AMOUNT = { light: 0.32, dark: 0.2 } as const;

export const brandTintBorder = (theme: { palette: { mode: string } }): string =>
  alpha(
    (theme.palette as any).brand?.[500] || "#8F68EA",
    TINT_BORDER_AMOUNT[theme.palette.mode === "light" ? "light" : "dark"],
  );

/**
 * Selected surface for an IN-PAGE sub-nav (the settings / labs left rail), at roughly 0.7x the
 * sidebar's alpha.
 *
 * Not because the colour is wrong — because the AREA is different. Measured, the tint is if anything
 * slightly *less* contrasty there: brand @0.20 is 1.276:1 / ΔE 20.26 on the sidebar surface
 * (#18181b) and 1.229:1 / ΔE 21.82 on the page (#09090b). What makes it read louder is that a
 * collapsed sidebar's selected row is a ~40x40 chip while a settings pill is a ~240x48 block — the
 * same tint over roughly seven times the area. Perceived intensity scales with area, so the larger
 * element needs less of it to carry the same weight.
 *
 * A sub-nav should also simply be quieter than the primary nav: it is the secondary level of
 * wayfinding on a page you are already inside.
 */
export const navSelectedSubtle = (theme: Theme): string =>
  alpha(
    (theme.palette as any).brand?.[500] || "#8F68EA",
    theme.palette.mode === "light" ? 0.09 : 0.14,
  );

/** Hover row surface — NEUTRAL, because hover is transient and can co-occur. */
export const navHover = (theme: Theme): string => theme.palette.action.hover;

/**
 * PRESSED row surface — one perceptible step past hover, and no further.
 *
 * A press lasts about 120ms. Its whole job is to confirm "the pointer went down on THIS
 * row", and the reader is already looking at the row, so the signal only has to be
 * distinguishable from hover — not legible from across the screen.
 *
 * Before this there was no `&:active` at all, so the press fell through to MUI's default
 * ripple: a dark splash sized for Material's much heavier surface treatment. Against this
 * app's near-white list surfaces it read as the row being SELECTED rather than pressed,
 * which is a different and persistent state (`navSelected`, brand-tinted) — so the two
 * momentarily looked the same and the press overstated itself.
 *
 * 1.75× the hover alpha is the smallest step that survives a 120ms flash without
 * approaching selected. Derived from `action.hover` rather than a literal so the two can
 * never drift apart: change hover and the press follows it, which is the property that
 * keeps "only slightly darker than hover" true rather than merely true today.
 */
const PRESSED_HOVER_MULTIPLIER = 1.75;

export const navPressed = (theme: Theme): string => {
  const hover = theme.palette.action.hover;
  // `action.hover` is an rgba() string in both modes. Scale its alpha in place rather
  // than re-deriving a colour, so this stays correct if the base hue is ever changed.
  const match = /^rgba?\(([^)]+)\)$/.exec(hover.trim());
  if (!match) return hover;
  const parts = match[1].split(",").map(p => p.trim());
  if (parts.length < 3) return hover;
  const alphaValue = parts.length > 3 ? Number(parts[3]) : 1;
  if (!Number.isFinite(alphaValue)) return hover;
  const next = Math.min(1, alphaValue * PRESSED_HOVER_MULTIPLIER);
  return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${next})`;
};

/**
 * Hover surface for a row that is ALREADY selected: unchanged, so hovering the current row does
 * not read as a state change — and specifically does NOT fall back to the neutral hover, which
 * would make the selected row look deselected while the pointer is over it.
 */
export const navSelectedHover = navSelected;

/**
 * The dim neutral that stays legible in BOTH colour modes.
 *
 * Mode-aware, and that is the entire reason it exists rather than using
 * `text.secondary` directly. `text.secondary` is `#a1a1aa` in BOTH modes: correct on a
 * dark surface (7.3:1 against the sidebar's #18181b, 7.47:1 against a card's #0f0f12)
 * and a failure on a light one, where the same grey measures **2.46:1** on #fafafa and
 * **2.56:1** on a white card — under WCAG AA's 4.5:1 for body text. Verified in the
 * browser, in light mode, not reasoned about; the first version of the sidebar's
 * rest-dim treatment shipped `text.secondary` and would have been an accessibility
 * regression for every light-mode user.
 *
 * Light gets `#52525b` — ~7.5:1 on #fafafa, measured 7.73:1 on a white card — clearly
 * dimmer than the near-black primary text it sits beside, without going illegible.
 *
 * ⚠️ THE PARAMETER IS STRUCTURAL, AND DELIBERATELY NOT MUI'S `Theme`.
 *
 * A `Theme` parameter is fine for a caller INSIDE this package, where there is one
 * `Theme`. From `apps/platform` it is not: the repo resolves two `@mui/material@5.18.0`
 * instances (one under `react@18.3.1`, one under `react@19.1.0`), so the app's `Theme`
 * and this package's are structurally identical and nominally distinct. Passing one
 * across that boundary produced a real `TS2345 … 'Theme' is not assignable to …
 * 'Theme'` plus a `TS2859 Excessive complexity comparing types 'Theme' and 'Theme'` —
 * measured, +1 over the platform's error baseline.
 *
 * Narrowing to the two fields actually read makes both instances satisfy it, which is
 * the correct shape anyway: this needs a mode and a token, not a theme. The duplicated
 * MUI install is the real defect and is NOT fixed here.
 */
export const dimNeutralText = (theme: {
  palette: { mode: string; text: { secondary: string } };
}): string => (theme.palette.mode === "light" ? "#52525b" : theme.palette.text.secondary);

/**
 * Resting text/icon colour for a nav or menu row — DIM, so hover has somewhere to go.
 *
 * The nav-shaped name for `dimNeutralText`, kept because it has call sites and because
 * "resting state for a row" is the reason the value is dim at all. It DELEGATES rather
 * than restating the ternary, so a change to the neutral cannot land on the sidebar and
 * miss the analytics marker, or the reverse.
 */
export const navRestText = (theme: Theme): string => dimNeutralText(theme);
